import { useEffect, useRef, useState, useCallback, useMemo } from "react";

export type Option = {
  id: number | string;
  label: string;
  value: string;
};

export type SortByOption = {
  value: string;
  label: string;
};

export type MultiCheckboxSelectProps = {
  label?: string;
  options: Option[];
  selectedValue?: (number | string)[];
  onChange?: (selectedIds: (number | string)[], isAllSelected: boolean) => void;
  placeholder?: string;
  isSortBy?: boolean;
  sortByOptions?: SortByOption[];
  sortByValue?: string;
  onSortByChange?: (value: string) => void;
  sortByLabel?: string;
  maxHeight?: string;
  selectAllLabel?: string;
  isSelectAll?: boolean;
  hideSelectAll?: boolean;
  disabled?: boolean;
  className?: string;
};

const MultiCheckboxSelect: React.FC<MultiCheckboxSelectProps> = ({
  label,
  options,
  selectedValue = [],
  onChange,
  isSortBy = false,
  sortByOptions = [],
  sortByValue,
  onSortByChange,
  sortByLabel = "Sort By",
  maxHeight = "max-h-80",
  selectAllLabel = "All",
  isSelectAll,
  hideSelectAll = false,
  disabled = false,
  className = "",
}) => {
  const [focusedIndex, setFocusedIndex] = useState<number>(0);
  const containerRef = useRef<HTMLFieldSetElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const selectAllRef = useRef<HTMLButtonElement>(null);
  const prevIsSelectAllRef = useRef<boolean | undefined>(undefined);
  const prevOptionLengthRef = useRef<number>(options.length);
  // --- scroll position preservation ---
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const savedScrollTop = useRef<number | null>(null);
  // Track last toggled option index for scroll
  const lastToggledIndex = useRef<number | null>(null);

  const isAllSelectedCheck = useMemo(
    () => selectedValue.length === options.length && options.length > 0,
    [selectedValue, options],
  );

  const updateSelection = useCallback(
    (newSelection: (number | string)[]) => {
      const willBeAllSelected =
        newSelection.length === options.length && options.length > 0;
      onChange?.(newSelection, willBeAllSelected);
    },
    [selectedValue, onChange, options.length],
  );

  // Sync with isSelectAll prop (must be after updateSelection is defined)
  useEffect(() => {
    if (
      isSelectAll !== undefined &&
      !selectedValue.length &&
      (prevIsSelectAllRef.current !== isSelectAll ||
        prevOptionLengthRef.current !== options.length)
    ) {
      prevIsSelectAllRef.current = isSelectAll;
      prevOptionLengthRef.current = options.length;
      const newSelection = isSelectAll ? options.map((o) => o.id) : [];
      updateSelection(newSelection);
    }
  }, [isSelectAll, selectedValue, options, updateSelection]);

  const handleSelectAll = useCallback(() => {
    // Preserve scroll position before triggering onChange
    if (scrollContainerRef.current) {
      savedScrollTop.current = scrollContainerRef.current.scrollTop;
    }
    lastToggledIndex.current = null; // No single option toggled
    const newSelection = isAllSelectedCheck ? [] : options.map((o) => o.id);
    updateSelection(newSelection);
  }, [isAllSelectedCheck, options, updateSelection]);

  const handleClick = useCallback(
    (index: number) => {
      if (disabled) return;
      // Preserve scroll position before triggering onChange
      if (scrollContainerRef.current) {
        savedScrollTop.current = scrollContainerRef.current.scrollTop;
      }
      lastToggledIndex.current = index;
      const id = options[index].id;
      const isSelected = selectedValue.includes(id);
      const newSelection = isSelected
        ? selectedValue.filter((x) => x !== id)
        : [...selectedValue, id];
      updateSelection(newSelection);
      setFocusedIndex(index);
    },
    [disabled, options, updateSelection, isAllSelectedCheck, selectedValue],
  );
  // Restore scroll position after re-render (e.g. after network call refreshes options)
  useEffect(() => {
    // If a specific option was toggled, scroll it into view
    if (lastToggledIndex.current !== null && itemRefs.current[lastToggledIndex.current]) {
      itemRefs.current[lastToggledIndex.current]?.scrollIntoView({ block: "nearest" });
      lastToggledIndex.current = null;
    } else if (savedScrollTop.current !== null && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = savedScrollTop.current;
      savedScrollTop.current = null;
    }
  });

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, currentIndex: number) => {
      if (disabled) return;

      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        e.preventDefault();

        if (e.key === "ArrowUp" && currentIndex === 0 && !hideSelectAll) {
          selectAllRef.current?.focus();
          setFocusedIndex(-1);
          return;
        }

        const direction = e.key === "ArrowDown" ? 1 : -1;
        const nextIndex = Math.max(
          0,
          Math.min(currentIndex + direction, options.length - 1),
        );

        setFocusedIndex(nextIndex);
        itemRefs.current[nextIndex]?.focus();
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleClick(currentIndex);
      }
    },
    [disabled, hideSelectAll, options.length, handleClick],
  );

  const handleSortByChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onSortByChange?.(e.target.value);
    },
    [onSortByChange],
  );

  const handleSelectAllKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleSelectAll();
      } else if (e.key === "ArrowDown" && options.length > 0) {
        e.preventDefault();
        itemRefs.current[0]?.focus();
        setFocusedIndex(0);
      }
    },
    [handleSelectAll, options],
  );

  // Auto-scroll focused item into view
  useEffect(() => {
    if (focusedIndex >= 0 && itemRefs.current[focusedIndex]) {
      itemRefs.current[focusedIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [focusedIndex]);

  // Memoized className values for performance
  const fieldsetClassName = useMemo(
    () =>
      `border border-border-gray border-x-0! p-0! m-0! bg-white shadow-sm ${
        disabled ? "opacity-50 cursor-not-allowed" : ""
      }`,
    [disabled],
  );

  const optionClassName = useMemo(
    () =>
      `${ hideSelectAll ? "pl-4!" : "pl-8!"}  py-2! flex items-center px-3 select-none transition-colors odd:bg-graphite-light even:bg-white w-full text-left border-0 ${
        disabled ? "cursor-not-allowed" : "cursor-pointer"
      }`,
    [disabled, hideSelectAll],
  );

  return (
    <div className={`flex flex-col ${className}`}>
      <div className="form-group">
        {label && <label className="mb-4!">{label}</label>}
        {isSortBy && sortByOptions.length > 0 && (
          <fieldset className="fieldset mb-2!">
            <legend className="radio-legend">{sortByLabel}</legend>
            {sortByOptions.map((option, index) => (
              <div key={option.value} className="mb-2!">
                <input
                  className="cursor-pointer"
                  id={`radio-sort-${index}`}
                  name="sort-by-group"
                  type="radio"
                  value={option.value}
                  checked={sortByValue === option.value}
                  onChange={handleSortByChange}
                  disabled={disabled}
                />
                <label
                  htmlFor={`radio-sort-${index}`}
                  className="label-ds2 cursor-pointer"
                >
                  {option.label}
                </label>
              </div>
            ))}
          </fieldset>
        )}
      </div>

      {options.length === 0 ? (
        <div className="border border-border-gray bg-white p-4 text-center text-border-gray">
          No options available
        </div>
      ) : (
        <fieldset
          ref={containerRef}
          className={fieldsetClassName}
          disabled={disabled}
          aria-label={label || "Select options"}
        >
          <div ref={scrollContainerRef} className={`${maxHeight} overflow-y-auto p-1`}>
            <div className="checkbox-container relative">
              {/* Select All */}
              {!hideSelectAll && (
                <button
                  ref={selectAllRef}
                  type="button"
                  onClick={handleSelectAll}
                  onKeyDown={handleSelectAllKeyDown}
                  disabled={disabled}
                  className="flex items-center bg-graphite-light pl-4! py-2! cursor-pointer w-full text-left border-0"
                >
                  <input
                    className="check-box"
                    readOnly
                    checked={isAllSelectedCheck}
                    id="checkbox-all"
                    name={selectAllLabel}
                    type="checkbox"
                    disabled={disabled}
                    aria-label={`Select ${selectAllLabel}`}
                    tabIndex={-1}
                  />
                  <label
                    className="cursor-pointer text-kp-blue-dark! pl-4.5! mt-0! pointer-events-none"
                    htmlFor="checkbox-all"
                  >
                    <span aria-hidden="true" className="pr-6!"></span>
                    {selectAllLabel}
                  </label>
                </button>
              )}

              {/* Options List */}
              {options.map((option, index) => {
                const checked = selectedValue.includes(option.id);
                return (
                  <button
                    key={`${option.id}-${option.label}`}
                    type="button"
                    ref={(el) => (itemRefs.current[index] = el)}
                    onClick={() => handleClick(index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    disabled={disabled}
                    className={optionClassName}
                  >
                    <input
                      className="check-box"
                      readOnly
                      checked={checked}
                      id={`checkbox-${option.id}`}
                      name={option.label}
                      type="checkbox"
                      disabled={disabled}
                      aria-label={option.label}
                      tabIndex={-1}
                    />
                    <label
                      className="cursor-pointer text-kp-blue-dark! pl-4.5! mt-0! pointer-events-none"
                      htmlFor={`checkbox-${option.id}`}
                    >
                      <span aria-hidden="true" className="pr-6!"></span>
                      {option.label}
                    </label>
                  </button>
                );
              })}
            </div>
          </div>
        </fieldset>
      )}
    </div>
  );
};

export default MultiCheckboxSelect;
